import * as React from 'react';
import * as moment from 'moment';
import * as paths from '../paths';

import {RouteComponentProps, withRouter} from 'react-router';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {ErrorResponse, Event, EventType, GetEventsRequest, User} from "../api/dtos/dtos";
import furoApi from "../api/FuroApi";
import {createDateRange, DateRange} from "../common/dtos";
import {createEventDto, EventDto} from "../event-details/dtos";
import Calendar from "./Calendar";
import CalendarHeader from "./header/CalendarHeader";
import accountProvider from "../account/AccountProvider";
import {cacheComponentStateForLocation} from "../utils/router-utils";
import {showDeleteSuccessNotification} from "../utils/dialogs";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";
import {isEventDateDisabled} from "../event-details/event-utils";

interface EventsPageState {
  dateRange: DateRange;
  events: EventDto[];
  salesUsers: User[];
  salesUser?: string;
  error?: ErrorResponse;
  showTodos: boolean;
}

interface EventsPageLocationState {
  showTodos: boolean;
  salesUser?: string;
  start?: string;
  end?: string;
}

class EventsPage extends React.Component<RouteComponentProps<{}, {}, EventsPageLocationState>, EventsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;


  constructor(props: RouteComponentProps<{}, {}, EventsPageLocationState>) {
    super(props);

    let defaultSalesUser: string | undefined;
    if (!accountProvider.isAdmin()) {
      defaultSalesUser = accountProvider.getUserName()!;
    }

    const locationState = this.props.location.state;

    this.state = {
      dateRange: (locationState?.start !== undefined && locationState?.end !== undefined) ? {
        start: moment(locationState.start),
        end: moment(locationState.end)
      } : createDateRange(moment()),
      events: [],
      salesUsers: [],
      salesUser: defaultSalesUser,
      showTodos: accountProvider.isSales(),

      // Override with cached state from Router
      ...this.props.location.state,
    }
  }

  public componentDidMount() {
    if (accountProvider.isAdmin()) {
      this.loadSalesUsers();
    } else {
      this.loadEvents();
    }
  }

  public render() {
    const {dateRange, events, salesUsers, salesUser, error, showTodos} = this.state;
    const eventsToShow = showTodos ? events : events.filter(event => event.type !== EventType.TODO);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.event, this)}
        </GlobalAlertContextConsumer>

        <CalendarHeader
          salesUsers={salesUsers}
          salesUser={salesUser}
          onSalesUserChange={this.onSalesUserChange}
          dateRange={dateRange}
          showTodos={showTodos}
          onDateRangeChange={this.onDateRangeChange}
          onAddEventClick={this.onAddEventClick}
          onShowTodosChange={this.onShowTodosChange}
        />

        <Calendar
          dateRange={dateRange}
          events={eventsToShow}
          onEditEventClick={this.onEventClick}
          onDeleteEventClick={this.onDeleteEventClick}
          onTimeRangeClick={this.onTimeRangeClick}
        />

        {error && <ErrorAlert style={{marginTop: 16}} error={error}/>}
      </>
    );
  }

  private loadSalesUsers = () => {
    const promise = furoApi.users.getSalesUsers()
      .then(this.onSalesUserLoaded)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onSalesUserLoaded = (salesUsers: User[]) => {
    const {salesUser} = this.state;
    let defaultSalesUser = salesUser;
    if (salesUser === undefined) {
      defaultSalesUser = salesUsers.length > 0 ? salesUsers[0].userName : undefined;
    }

    this.setState({salesUsers, salesUser: defaultSalesUser}, this.loadEvents);
  }

  private onSalesUserChange = (salesUser: string) => {
    this.setState({salesUser}, this.loadEvents);
  }

  private onShowTodosChange = (checked: boolean) => {
    this.setState({showTodos: checked}, this.saveComponentStateForLocation);
  }

  private onDateRangeChange = (dateRange: DateRange) => {
    this.setState({dateRange}, this.loadEvents);
  }

  private saveComponentStateForLocation = () => {
    cacheComponentStateForLocation(this.props, {
      salesUser: this.state.salesUser,
      showTodos: this.state.showTodos,
      start: this.state.dateRange.start.toJSON(),
      end: this.state.dateRange.end.toJSON(),
    });
  }

  private loadEvents() {
    const {dateRange, salesUser} = this.state;
    if (salesUser === undefined) {
      return;
    }

    const request: GetEventsRequest = {dateRange, userName: salesUser};
    const promise = furoApi.events.getEvents(request)
      .then(this.onEventsLoaded)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onEventsLoaded = (events: Event[]) => {
    const eventDtos = events.map((e) => createEventDto(e));
    this.setState({events: eventDtos}, this.saveComponentStateForLocation);
  }

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  }

  private onEventClick = (event: EventDto) => {
    this.props.history.push(paths.viewEventPath(event.id));
  }

  private onDeleteEventClick = (event: EventDto) => {
    const promise = furoApi.events.delete(event.id!)
      .then(this.onEventDeleted)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onEventDeleted = () => {
    showDeleteSuccessNotification();
    this.alertController?.refreshAlertCount();
    this.loadEvents();
  }

  private onTimeRangeClick = (timeRange: DateRange) => {
    if (!isEventDateDisabled(accountProvider.getRole()!, timeRange.start)) {
      const {salesUser} = this.state;
      this.props.history.push(paths.createEventPathWithArgs(salesUser, timeRange));
    }
  }

  private onAddEventClick = () => {
    const {salesUser} = this.state;
    this.props.history.push(paths.createEventPathWithArgs(salesUser));
  };
}

export default withRouter(EventsPage);



// WEBPACK FOOTER //
// ./src/event/EventsPage.tsx